// SPDX-License-Identifier: AGPL-3.0-or-later

import styles from '@app/features/app/components/layout/NativeTitlebar.module.css';
import FocusRing from '@app/features/ui/focus_ring/FocusRing';
import {getElectronAPI, type NativePlatform} from '@app/features/ui/utils/NativeUtils';
import FluxerWordmark from '@app/media/images/fluxer-wordmark.svg?react';
import {useLingui} from '@lingui/react/macro';
import {CopySimpleIcon, MinusIcon, SquareIcon, XIcon} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import type React from 'react';
import {useEffect, useState} from 'react';

interface NativeTitlebarProps {
	platform: NativePlatform;
}
export const NativeTitlebar: React.FC<NativeTitlebarProps> = ({platform}) => {
	const {t} = useLingui();
	const [isMaximized, setIsMaximized] = useState(false);
	useEffect(() => {
		const electronApi = getElectronAPI();
		if (!electronApi?.onWindowMaximizeChange) return;
		const unsubscribe = electronApi.onWindowMaximizeChange((maximized: boolean) => {
			setIsMaximized(maximized);
		});
		return () => {
			unsubscribe();
		};
	}, []);
	const handleMinimize = () => {
		const electronApi = getElectronAPI();
		electronApi?.windowMinimize?.();
	};
	const handleToggleMaximize = () => {
		const electronApi = getElectronAPI();
		if (!electronApi?.windowMaximize) return;
		electronApi.windowMaximize();
	};
	const handleClose = () => {
		const electronApi = getElectronAPI();
		electronApi?.windowClose?.();
	};
	const handleDoubleClick = () => {
		handleToggleMaximize();
	};
	return (
		<div
			role="group"
			className={styles.titlebar}
			onDoubleClick={handleDoubleClick}
			data-platform={platform}
			data-native-titlebar=""
		>
			<div className={styles.left}>
				<FluxerWordmark className={styles.wordmark} />
			</div>
			<div className={styles.spacer} />
			<div className={styles.controls}>
				<FocusRing offset={-2}>
					<button
						type="button"
						className={styles.controlButton}
						onClick={handleMinimize}
						aria-label={t`Minimize window`}
					>
						<MinusIcon weight="bold" />
					</button>
				</FocusRing>
				<FocusRing offset={-2}>
					<button
						type="button"
						className={styles.controlButton}
						onClick={handleToggleMaximize}
						aria-label={isMaximized ? t`Restore window` : t`Maximize window`}
					>
						{isMaximized ? <CopySimpleIcon weight="bold" /> : <SquareIcon weight="bold" />}
					</button>
				</FocusRing>
				<FocusRing offset={-2}>
					<button
						type="button"
						className={clsx(styles.controlButton, styles.closeButton)}
						onClick={handleClose}
						aria-label={t`Close window`}
					>
						<XIcon weight="bold" />
					</button>
				</FocusRing>
			</div>
		</div>
	);
};
